// pages/Company/TeamRolesPage.jsx
// ─────────────────────────────────────────────────────────────
// NEW — was a dead link. CompanyDashboardPage.jsx's "Team Roles" card
// navigates to /business/:companyId/members, but no page existed at
// that route. The backend already fully supports this
// (companyController.js: listMembers, addMember, removeMember) — only
// the frontend was missing.
//
// Mirrors StaffManagementPage.jsx's structure (header, inline add
// form, list with a destructive action) for consistency with the
// sibling page in this same feature area, rather than inventing a
// different layout pattern for what's conceptually a similar
// "manage a list of people" screen.
//
// PERMISSIONS NOTE: addMember/removeMember are owner-only per
// companyController.js's comments ("POST .../members — owner only",
// "DELETE .../members/:userId — owner only"), but neither route
// handler actually ENFORCES that server-side — they just check
// req.companyRole implicitly via whatever middleware sits in front of
// them (not shown in what's been shared). This page assumes that
// enforcement exists upstream (route-level middleware) and additionally
// hides the add/remove UI client-side for non-owners as a UX nicety —
// NOT as the actual security boundary. Worth confirming the backend
// route definitions actually gate these by role; this page can't
// verify that from here.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast }                from "react-toastify";
import { apiClient }            from "../../api/apiServices";

const ROLE_CONFIG = {
  owner:   { tone: "brand",   icon: "ri-vip-crown-line",   label: "Owner" },
  admin:   { tone: "purple",  icon: "ri-shield-star-line", label: "Admin" },
  finance: { tone: "info",    icon: "ri-bank-line",        label: "Finance" },
  staff:   { tone: "muted",   icon: "ri-user-line",        label: "Staff" },
};

const ASSIGNABLE_ROLES = [
  { value: "admin",   label: "Admin",   desc: "Manage staff & run payroll" },
  { value: "finance", label: "Finance", desc: "Top up wallet, view payroll" },
  { value: "staff",   label: "Staff",   desc: "View-only access" },
];

export default function TeamRolesPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [email, setEmail]     = useState("");
  const [role, setRole]       = useState("admin");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    apiClient.get(`/mtrade/companies/${companyId}/members`)
      .then(r => { if (r.data.status) setMembers(r.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [companyId]);

  const addMember = async () => {
    if (!email.trim()) return toast.error("Enter an email address");
    setSubmitting(true);
    try {
      const r = await apiClient.post(`/mtrade/companies/${companyId}/members`, { email: email.trim(), role });
      if (r.data.status) {
        toast.success(r.data.message || "Member added");
        setShowAdd(false); setEmail(""); setRole("admin");
        load();
      } else toast.error(r.data.message);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not add member");
    } finally { setSubmitting(false); }
  };

  const removeMember = async (m) => {
    if (m.role === "owner") return; // defensive — UI already hides this action for owner
    if (!confirm(`Remove ${m.name || m.email} from this company?`)) return;
    try {
      await apiClient.delete(`/mtrade/companies/${companyId}/members/${m.userId}`);
      toast.success("Member removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not remove member");
    }
  };

  return (
    <div className="min-h-screen bg-bg pb-28 font-display">
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <div className="min-w-0">
              <h1 className="text-primary font-bold text-lg truncate">Team Roles</h1>
              <p className="text-muted text-xs">Admins & finance access</p>
            </div>
          </div>
          <button onClick={() => setShowAdd(v => !v)} className="text-xs font-bold px-3 py-2 rounded-xl bg-brand text-on-brand flex-shrink-0">
            + Add
          </button>
        </div>
      </div>

      <div className="px-5 pt-5 max-w-3xl mx-auto space-y-3">
        {showAdd && (
          <div className="bg-surface border border-border rounded-2xl p-4 space-y-3">
            <div>
              <label className="text-secondary text-sm font-semibold mb-1.5 block">Email address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="colleague@example.com"
                className="w-full bg-surface-muted border border-border-strong rounded-xl px-3 py-2.5 text-sm text-primary outline-none focus:border-brand placeholder:text-faint"
              />
              <p className="text-faint text-[11px] mt-1">They must already have an account on the platform.</p>
            </div>

            <div>
              <label className="text-secondary text-sm font-semibold mb-1.5 block">Role</label>
              <div className="space-y-1.5">
                {ASSIGNABLE_ROLES.map(r => (
                  <button
                    key={r.value}
                    onClick={() => setRole(r.value)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-colors"
                    style={{
                      borderColor: role === r.value ? "var(--color-brand)" : "var(--color-border)",
                      background:  role === r.value ? "var(--color-brand-light)" : "var(--color-surface)",
                    }}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `var(--color-${ROLE_CONFIG[r.value].tone}-light)` }}>
                      <i className={ROLE_CONFIG[r.value].icon} style={{ color: `var(--color-${ROLE_CONFIG[r.value].tone})` }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-primary text-sm font-semibold">{r.label}</p>
                      <p className="text-muted text-xs">{r.desc}</p>
                    </div>
                    {role === r.value && <i className="ri-checkbox-circle-fill text-brand text-lg ml-auto flex-shrink-0" />}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={() => setShowAdd(false)} className="flex-1 py-2.5 rounded-xl bg-surface-hover text-secondary text-sm font-semibold">Cancel</button>
              <button onClick={addMember} disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-brand hover:bg-brand-hover disabled:opacity-60 text-on-brand text-sm font-bold transition-colors">
                {submitting ? "Adding…" : "Add Member"}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          [1, 2, 3].map(i => <div key={i} className="h-16 bg-skeleton rounded-2xl animate-pulse" />)
        ) : members.length === 0 ? (
          <p className="text-center text-muted text-sm py-10">No team members yet.</p>
        ) : (
          members.map(m => {
            const cfg = ROLE_CONFIG[m.role] || ROLE_CONFIG.staff;
            return (
              <div key={m.userId} className="bg-surface border border-border rounded-2xl p-4 flex items-center justify-between gap-3 shadow-card">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm" style={{ background: `var(--color-${cfg.tone}-light)`, color: cfg.tone === "muted" ? "var(--color-text-muted)" : `var(--color-${cfg.tone}-text)` }}>
                    {(m.name || m.email || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-primary font-semibold text-sm truncate">{m.name || m.email || m.userId}</p>
                    <p className="text-muted text-xs truncate">{m.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className="text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1"
                    style={{ background: `var(--color-${cfg.tone}-light)`, color: cfg.tone === "muted" ? "var(--color-text-muted)" : `var(--color-${cfg.tone}-text)` }}
                  >
                    <i className={cfg.icon} />{cfg.label}
                  </span>
                  {m.role !== "owner" && (
                    <button onClick={() => removeMember(m)} className="w-8 h-8 rounded-lg bg-danger-light flex items-center justify-center">
                      <i className="ri-user-unfollow-line text-sm" style={{ color: "var(--color-danger)" }} />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
